// @ts-ignore
import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useState
} from "react";

import {
    useFetchUserSegmentData,
    useSearchParameters,
    useUserSegmentationStatus
} from "./";

export type JiraTemplateChoice = "kanban" | "scrum" | "business" | undefined;

const jiraTemplateChoiceTypeCheck = (
    s: string | undefined
): JiraTemplateChoice => {
    switch (s) {
        case "kanban":
            return "kanban";
            break;
        case "scrum":
            return "scrum";
            break;
        case "business":
            return "business";
            break;
        default:
            return undefined;
    }
};

type JiraTemplateChoiceProps = {
    shouldShowJiraTemplateChoiceScreen: boolean;
    loadingUserSegmentData: boolean;
    templateChoice?: JiraTemplateChoice;
    setTemplateChoice: (templateChoice?: JiraTemplateChoice) => void;
    templateChoiceError: Error | undefined;
    setTemplateChoiceError: (e: Error) => void;
};

export const JiraTemplateChoiceContext = createContext<JiraTemplateChoiceProps>(
    {
        shouldShowJiraTemplateChoiceScreen: false,
        loadingUserSegmentData: false,
        templateChoice: undefined,
        setTemplateChoice: () => undefined,
        templateChoiceError: undefined,
        setTemplateChoiceError: () => undefined
    }
);

export const JiraTemplateChoiceProvider = ({
    children
}: PropsWithChildren<unknown>) => {
    const { products } = useSearchParameters();
    const { canShowJiraTemplateChoiceScreen, loadingUserSegmentData } =
        useCanShowJiraTemplateChoiceScreen(products);

    const { templateChoice: templateChoiceQueryParam } = useSearchParameters();

    const [templateChoice, setTemplateChoice] = useState<
        JiraTemplateChoice | undefined
    >(jiraTemplateChoiceTypeCheck(templateChoiceQueryParam));

    const [templateChoiceError, setTemplateChoiceError] = useState<
        Error | undefined
    >();

    const userHasPickedTemplate = templateChoice !== undefined;

    // temporarily turn off for local dev (not including storybook) (TODO: remove this when user segmentation runs locally)
    const isLocalDevEnvironment =
        window.location.hostname === "localhost" && !window.IS_STORYBOOK;

    const shouldShowJiraTemplateChoiceScreen =
        templateChoiceError === undefined &&
        canShowJiraTemplateChoiceScreen &&
        !userHasPickedTemplate &&
        !isLocalDevEnvironment;

    return (
        <JiraTemplateChoiceContext.Provider
            value={{
                templateChoice,
                loadingUserSegmentData,
                setTemplateChoice,
                shouldShowJiraTemplateChoiceScreen,
                templateChoiceError,
                setTemplateChoiceError
            }}
        >
            {children}
        </JiraTemplateChoiceContext.Provider>
    );
};
/* The user must pass the following conditions to see the template choice screen
   - !hasTemplateRecommendation: Supplied via a query param, there must be no `templateId`
   - isProvisioningOnlyJSW: Provisioning only JSW and no other products
   - isN2NUser: if n2n=true query param (new user signing up for first instance)
   - !isBusinessSegmentUser: The user must not be counted as a business user by user segmentation
   - isUserSegDon: if the user seg screen was marked as finished or hidden by a query param
   - !fetchError: This hook must access /gateway/api/me to check user segmentation and this call must succeed
     in order for the check above to be valid
 */
export const useCanShowJiraTemplateChoiceScreen = (
    products: readonly string[]
) => {
    const { shouldShowUserSegmentationPage, userSegmentationPageIsFinished } =
        useUserSegmentationStatus(products);

    const { templateId } = useSearchParameters();
    const hasTemplateRecommendation = Boolean(templateId);
    const isProvisioningOnlyJSW =
        products.length === 1 && products[0] === "jira-software.ondemand";

    const { fetched, isBusinessSegmentUser } = useFetchUserSegmentData(
        userSegmentationPageIsFinished
    );

    const canShowJiraTemplateChoiceScreen =
        isProvisioningOnlyJSW &&
        !hasTemplateRecommendation &&
        !shouldShowUserSegmentationPage &&
        !isBusinessSegmentUser;

    return {
        canShowJiraTemplateChoiceScreen,
        loadingUserSegmentData: !fetched
    };
};

export const useJiraTemplateChoice = () => {
    return useContext(JiraTemplateChoiceContext);
};
